/**
 * TypeScript-Typen für den Snapshot-Contract.
 * Abgeleitet aus docs/SCHEMA_MAPPING.md §2 und der echten JSON-Struktur
 * (schema/snapshot.schema.json). MAßGEBLICH sind die Kurznamen (nkm..ju).
 *
 * HARTE Regeln aus SCHEMA_MAPPING §5:
 *  - Jeder bwa_block-Wert ist `number | null`. null-IST ist NORMALFALL.
 *  - quarterly.qX kann ein Quarter-Objekt ODER `null` sein (defensive).
 *  - bewertung kann `null` sein (reduzierte Entität).
 */

/** Die 19 BWA-Keys in fachlicher BWA-Reihenfolge (Z5–Z23), NICHT alphabetisch. */
export const BWA_KEYS = [
  'nkm',
  'erloese_nk',
  'sonst_umsatz',
  'umlegb_nk',
  'nicht_umlegb_nk',
  'instandhaltung',
  'personal',
  'wb_mietford',
  'interne_bf',
  'evv', // Z14 Zwischensumme EvV
  'verwaltung',
  'interne_verw_umlage',
  'sonst_betr',
  'zinsen_bank',
  'sonst_zbe',
  'ebtda', // Z20 Zwischensumme EBTDA
  'afa',
  'steuern',
  'ju', // Z23 Bottom-Line JÜ
] as const;

export type BwaKey = (typeof BWA_KEYS)[number];

/** Frontend-Labels je BWA-Key (SCHEMA_MAPPING §2.2). */
export const BWA_LABELS: Record<BwaKey, string> = {
  nkm: 'Nettokaltmiete (NKM)',
  erloese_nk: 'Erlöse Nebenkosten',
  sonst_umsatz: 'sonstige Umsatzerlöse',
  umlegb_nk: 'umlegbare Nebenkosten',
  nicht_umlegb_nk: 'nicht umlegbare Nebenkosten',
  instandhaltung: 'Instandhaltung',
  personal: 'Personalaufwand',
  wb_mietford: 'Wertberichtigung Mietforderungen',
  interne_bf: 'interne Betriebsführung',
  evv: 'Ergebnis Vermietung/Verpachtung (EvV)',
  verwaltung: 'Verwaltungskosten',
  interne_verw_umlage: 'interne Verwaltungsumlagen',
  sonst_betr: 'sonst. betr. Aufw./Ertr.',
  zinsen_bank: 'Zinsen Bankdarlehen',
  sonst_zbe: 'sonstiges Zinsergebnis',
  ebtda: 'EBTDA',
  afa: 'Abschreibungen (AfA)',
  steuern: 'Steuern',
  ju: 'Jahresüberschuss (JÜ)',
};

/** Keys, die im UI als Zwischensummen/Ergebniszeilen hervorgehoben werden. */
export const BWA_SUBTOTAL_KEYS: ReadonlySet<BwaKey> = new Set(['evv', 'ebtda', 'ju']);

/** Ein bwa_block: exakt die 19 Keys, jeweils number | null. */
export type BwaBlock = Record<BwaKey, number | null>;

/** Quartals-Status (Schema 2.0, D-031 §2.3). */
export type QuarterStatus = 'closed' | 'active' | 'forecast';

export interface Quarter {
  status: QuarterStatus;
  ist: BwaBlock;
  soll: BwaBlock;
  abweichung_pct: BwaBlock;
  /** D-041: richtungs-korrigierte Abweichung (günstig>0 = grün). Optional (alter Cache-Stand
   *  ohne das Feld → Ampel neutral). */
  abweichung_guenstig_pct?: BwaBlock;
}

/** D-041: Kumulierung über abgeschlossene Quartale (Single-Source aus dem Export). */
export interface QuarterlyKumuliert {
  quartale: string[];
  ist: BwaBlock;
  soll: BwaBlock;
  abweichung_pct: BwaBlock;
  abweichung_guenstig_pct: BwaBlock;
}

/** quarterly.qX: Objekt ODER null (SCHEMA_MAPPING §0.5). */
export type QuarterOrNull = Quarter | null;

export interface Quarterly {
  q1_2026: QuarterOrNull;
  q2_2026: QuarterOrNull;
  q3_2026: QuarterOrNull;
  q4_2026: QuarterOrNull;
}

export interface Jahr {
  ist: BwaBlock;
  prognose: BwaBlock;
  soll: BwaBlock;
}

export interface CashflowBlock {
  ebtda: number | null;
  capex: number | null;
  free_cf: number | null;
  tilgung: number | null;
  gd_change: number | null;
  netto_cf: number | null;
  cash_end?: number | null; // nur in q1_ytd
}

/** D-041: YTD-Cashflow — reine Ist-Anteile bis zum jüngsten Stichtag. */
export interface YtdCashflow extends CashflowBlock {
  stichtag: string;               // "MM/2026" (jüngster Ist-Monat)
  freies_cash_stichtag?: number | null;
}

export interface Cashflow {
  vj: CashflowBlock;
  /** DEPRECATED (D-041): durch `ytd` abgelöst, für Cache-Rückwärtskompat erhalten. */
  q1_ytd: CashflowBlock;
  prognose: CashflowBlock;
  /** D-041: YTD-Cashflow. Optional (alter Cache-Stand ohne das Feld → Fallback auf q1_ytd). */
  ytd?: YtdCashflow;
}

export interface Loan {
  label: string;
  boy: number | null;
  tilgung: number | null;
}

export interface Darlehen {
  loans: Loan[]; // variabel 0–3
  gd_boy: number | null;
  bank_boy: number | null;
}

export interface EquityRoeVj {
  ebtda: number | null;
  roe_eur: number | null;
  roe_pct: number | null;
  gez: number | null;
  krl: number | null;
  gd: number | null;
  equity_intern: number | null;
}

export interface EquityRoePrognose {
  ebtda: number | null;
  roe_eur: number | null;
  roe_pct: number | null;
  equity_intern: number | null;
}

export interface EquityRoe {
  vj: EquityRoeVj;
  prognose: EquityRoePrognose;
}

export interface BewertungBlock {
  value_125: number | null;
  value_135: number | null;
  bank: number | null;
  nav: number | null;
  em: number | null;
  em_pct: number | null;
}

/** bewertung: Objekt ODER null (reduzierte Entität, valuation_applicable=false). */
export type Bewertung = { vj: BewertungBlock; prognose: BewertungBlock } | null;

/**
 * D-072: Quartals-IST je IC-Konto als Differenz aufeinanderfolgender SuSa-Stichtage.
 * `null` heißt „kein Stichtag in diesem Quartal" — bewusst KEINE 0, die wäre von
 * einer echten Null nicht unterscheidbar. Stand 05/2026: q1/q2 belegt, q3/q4 null.
 *
 * String-Keys q1..q4 (nicht 1..4): `json.dumps` wandelt int-Keys still in Strings,
 * der Round-Trip wäre sonst nicht gleich (D-072).
 */
export interface IntercompanyQIst {
  q1: number | null;
  q2: number | null;
  q3: number | null;
  q4: number | null;
}

export interface IntercompanyRow {
  konto: string;
  label: string;
  /**
   * ABGELEITETER ALIAS auf `q_ist.q1` (D-072) — keine zweite Rechnung. Bleibt für
   * Rückwärtskompatibilität erhalten; für eine Quartalsanzeige ist `q_ist` zu lesen.
   */
  q1: number | null;
  vj: number | null;
  /** D-080, additiv. Q-SOLL = JA 2025 / 4 (D-057-Definition); null ohne VJ-SuSa. */
  soll_q?: number | null;
  /** D-080, additiv. Jahres-IST (Summe belegter Quartale, null ohne Belegung) und
   *  Jahres-PROGNOSE. Seit D-085 (SR 69252ef): prognose = Ist-Monatsrate × 12
   *  (vorher: je Quartal Ist wo belegt, sonst Q-Soll). */
  jahr?: { ist: number | null; prognose: number };
  /** D-085, additiv: Ist-Monatsrate des laufenden Jahres (YTD-Ist ÷ Ist-Monate). */
  rate_monat?: number;
  /**
   * D-085, additiv: Prognose je Quartal — abgeschlossen = Ist; aktiv = Ist der
   * belegten Monate + Rate × Restmonate; künftig = Rate × 3. Fehlt es (Alt-Snapshot),
   * zeigt die UI weiter das Q-Soll (D-080).
   */
  q_prognose?: { q1: number; q2: number; q3: number; q4: number };
  /** D-085, additiv: im VJ bebucht, 2026 bisher nicht (Hinweis `ic_konto_unbebucht`). */
  unbebucht_2026?: boolean;
  /**
   * D-072, additiv. OPTIONAL, weil ein älterer Snapshot-Stand (Backend-Cache) das
   * Feld noch nicht führt — dieselbe Konvention wie bei allen additiven Blöcken.
   *
   * Optional + strictNullChecks ist hier die STÄRKERE Zusicherung: jeder Leser muss
   * den Fehlfall behandeln und kann nicht blind auf `row.q_ist.q3` zugreifen. Ein
   * `required` würde genau das erlauben und Q3/Q4 als 0 durchgehen lassen.
   * Fehlt das Feld, blendet die UI die Quartalsspalten aus (statt 0 zu zeigen).
   */
  q_ist?: IntercompanyQIst;
  /**
   * D-072, additiv: `true`, wenn das Konto in KEINER SuSa des laufenden Jahres eine
   * Zeile führt und mit 0 mitläuft. Muss sichtbar gemacht werden — eine 0 aus
   * „Konto fehlt" ist keine fachliche Null.
   */
  fehlt_2026?: boolean;
}

/**
 * D-036 „Kennzeichnen statt Korrigieren" — additive, optionale Felder je Gesellschaft.
 * Struktur exakt aus lbu-standortreporting/scripts/lib/retro.py::snapshot_felder().
 */
export interface RetroHinweis {
  monat: number; // 1..12, Buchungs-/Ausweismonat (kein separater Ursprungsmonat)
  pruefpaar: [string, string] | null; // (Vormonat-Periode, Neu-Periode), Format "MM/YYYY"
  kategorien: Record<string, number>; // BWA-Kategoriename (CAT2LINE) -> Retro-EUR
  nicht_zuordenbar: number;
}

export type PruefluckeArt = 'ERSTER_STICHTAG' | 'MONATSLUECKE';

export interface Pruefluecke {
  monat: number;
  grund: string | null;
  art: PruefluckeArt | null;
}

export type AfaStufe = 'WARNUNG' | 'INFO';

export interface AfaWarnung {
  stufe: AfaStufe;
  text: string;
  ytd_afa: number | null;
  erwartet: number | null;
  monatssatz: number | null;
  monate: number | null;
}

// ---------------------------------------------------------------------------
// R2 Phase 4b — additive Blöcke (Schema 2.0, KEIN Bump). Alle Felder optional:
// fehlend = Snapshot ohne das Feld (alter Cache) → UI blendet aus. Struktur 1:1
// aus lbu-standortreporting/scripts/export_dashboard_data.py + lib/*.py.
// ---------------------------------------------------------------------------

/** Bankbestände je Gesellschaft (D-050). Bank = frei; Kaution/Capex = gebunden.
 *  Das GANZE Objekt ist null, wenn keine 2026-SuSa vorliegt. */
export interface BankBestaende {
  bank: number | null;
  kaution: number | null;
  capex: number | null;
  stichtag: string; // "MM/2026"
  unklar_konten: number[]; // 18xx ohne klare Zuordnung, kann [] sein
}

/** Intercompany-Bezüge aggregiert (D-051). Ganzes Objekt null ohne 2026-SuSa.
 *  Vertragstreu mitgeführt; wird von KEINEM R2-UI-Punkt gerendert (additiv). */
export interface IcBezuege {
  cfs: number | null;
  vu: number | null;
  gewerbeenergien: number | null;
  flag_konten: number[];
}

/** FCFE-Bausteine (fcfe.py LINES) in fachlicher Reihenfolge, NICHT alphabetisch. */
export const FCFE_LINES = [
  'ebtda',
  'aktivierung',
  'bank_auf',
  'bank_rueck',
  'gd_auf',
  'gd_rueck',
  'ausschuettung',
  'fcf',
  'fcfe',
] as const;
export type FcfeLine = (typeof FCFE_LINES)[number];

/** basis: "ist" (status closed/active) | "vj12" (Prognose aus VJ 12/2025-Näherung). */
export type FcfeBasis = 'ist' | 'vj12';

/** Ein FCFE-Quartal: status + basis + die 9 Bausteine FLACH (Spread, nicht verschachtelt). */
export type FcfeQuartal = {
  status: QuarterStatus;
  basis: FcfeBasis;
} & Record<FcfeLine, number | null>;

/** fcfe.jahr.<line>: nur ist + prognose (KEIN soll im Snapshot). prognose kann null sein. */
export interface FcfeJahrLine {
  ist: number | null;
  prognose: number | null;
}

/** D-052/D-053: Free Cashflow to Equity je Quartal + Jahr. r003_naeherung TOP-LEVEL. */
export interface Fcfe {
  lines: FcfeLine[];
  quartale: Partial<Record<QuarterKey, FcfeQuartal>>;
  jahr: Record<FcfeLine, FcfeJahrLine>;
  /** true = SuSa-Lücke (R-003-Näherung, aktuell nur SL MG). Fußnote im UI. */
  r003_naeherung: boolean;
}

/** D-040: eine Quartalssumme je Anlagenkonto. wert=null ⇒ „nicht ableitbar", NIE 0. */
export interface AktQuartal {
  wert: number | null;
  ableitbar: boolean;
}

export interface AktKonto {
  konto: number;
  label: string;
  im_bau: boolean; // Kto 700 „Anlagen im Bau"
  ytd_anfang: number | null;
  summe_ist: number | null;
  /** NUR abgeschlossene Quartale (status closed) — aktuell nur q1_2026. */
  quartale: Partial<Record<QuarterKey, AktQuartal>>;
}

export interface Aktivierungen {
  konten: AktKonto[]; // kann [] sein, nie null
}

export type HinweisSchweregrad = 'INFO' | 'HINWEIS' | 'WARNUNG';

/** Ein Kommentar/Hinweis (build_hinweise). Alle vier Felder immer str. */
export interface Hinweis {
  kriterium: string;
  schweregrad: HinweisSchweregrad;
  text: string;
  quelle: string;
}

/**
 * D-084 (Prompt 127, Aufgabe 2): KI-Analyse je Gesellschaft. Feldformat laut Prompt 127
 * ANGENOMMEN — der Erzeuger (SR-Repo) ist parallel in Arbeit.
 * `text` ist Markdown-frei, Absätze durch `\n` getrennt.
 * `abweichung_ebtda_pct` [Annahme]: Dezimal wie alle *_pct-Felder (−0.04 → „-4,0 %").
 */
export interface KiAnalyse {
  text: string;
  abweichung_ebtda_pct: number | null;
  treiber: string[];
  auffaelligkeiten: string[];
  erzeugt: string; // ISO 8601
  modell: string;
}

/** D-084: ein Eintrag aus snapshot.ki_summary (sortiert nach |abweichung| absteigend). */
export interface KiSummaryEintrag {
  slug: string;
  short_name: string;
  abweichung_ebtda_pct: number | null;
  kernpunkte: string[];
}

export interface Entity {
  name: string;
  short_name: string;
  type: string;
  is_property_holding: boolean;
  klasse: string;
  valuation_applicable: boolean;
  vorjahr_2025: BwaBlock;
  quarterly: Quarterly;
  /** D-041: Σ über abgeschlossene Quartale (Single-Source aus dem Export). Optional. */
  quarterly_kumuliert?: QuarterlyKumuliert;
  jahr_2026: Jahr;
  cashflow: Cashflow;
  darlehen: Darlehen;
  equity_roe: EquityRoe;
  bewertung: Bewertung;
  intercompany: IntercompanyRow[]; // variabel 3–7
  // D-036 (additiv, optional): fehlend/leer = keine Nachbuchungen/Prüfhinweise.
  retro_hinweise?: RetroHinweis[];
  pruefluecken?: Pruefluecke[];
  afa_warnung?: AfaWarnung | null;
  // R2 Phase 4b (additiv, optional): fehlend = alter Cache-Stand ohne das Feld.
  bank_bestaende?: BankBestaende | null; // null = keine 2026-SuSa
  ic_bezuege?: IcBezuege | null; // null = keine 2026-SuSa (UI: ungenutzt)
  fcfe?: Fcfe;
  aktivierungen?: Aktivierungen;
  hinweise?: Hinweis[];
  /** D-084, additiv: fehlt im alten Snapshot → keine KI-Sektion. */
  ki_analyse?: KiAnalyse | null;
  /**
   * D-084-Nachtrag (Prompt 128 §1): KEIN Snapshot-Feld — das Backend setzt es in
   * /api/asset aus pph["members"] (kanonisch). Fehlt es (älteres Backend), wird
   * nichts ausgeblendet.
   */
  portfolio_mitglied?: boolean;
}

export interface ExcludedEntities {
  reason: string;
  entries: string[];
}

/** Vollstaendigkeits-Status je Berichtsmonat (S2.4 / D-047). */
export interface Vollstaendigkeit {
  soll_monat: number | null; // erwarteter Monat (juengster vorhandener), 1..12
  vorhanden: Record<string, number>; // short_name -> juengster Monat
  fehlend: string[]; // short_names unter Soll oder ganz ohne Daten
}

/** Antwort von GET /api/meta. */
export interface Meta {
  schema_version: string;
  generated_at: string; // ISO 8601 +TZ
  data_stand: string; // z.B. "SuSa 03/2026"
  methodik_version: string;
  excluded: ExcludedEntities;
  // Optional (alter Cache-Stand ohne das Feld -> Fallback/kein Hinweis, D-047).
  vollstaendigkeit?: Vollstaendigkeit | null;
  // D-089, additiv: Berichts-/Vorjahr. null/fehlend -> Fallback in lib/jahre.tsx.
  berichtsjahr?: number | null;
  vorjahr?: number | null;
  jahreswechsel?: Jahreswechsel | null;
}

/** Wartegrund einer Gesellschaft vor dem nächsten Jahreswechsel (D-089). */
export interface JahreswechselWartet {
  gesellschaft: string;
  grund: string;
}

/**
 * Jahreswechsel-Status aus dem Snapshot (D-089, SR-Repo jahreswechsel.py).
 * Die Schema-Schlüssel q1_2026 … / vorjahr_2025 / jahr_2026 bleiben als Vertrag
 * unverändert und bedeuten „Quartal/Jahr des Berichtsjahrs" bzw. „Vorjahr".
 */
export interface Jahreswechsel {
  berichtsjahr: number;
  vorjahr: number;
  override: boolean;
  umgestellt_am: string | null; // ISO 8601
  kontrollbericht: string | null; // Dateiname im SR-Repo output/jahreswechsel/
  naechstes_jahr: number | null;
  wartet_auf: JahreswechselWartet[];
}

export type QuarterKey = 'q1_2026' | 'q2_2026' | 'q3_2026' | 'q4_2026';

/** Portfolio-Quartal: nur ist + soll, KEIN abweichung_pct (§2.5). */
export interface PortfolioQuarter {
  ist: BwaBlock;
  soll: BwaBlock;
}

export type PortfolioQuarterOrNull = PortfolioQuarter | null;

export interface PortfolioQuarterlyAggregated {
  q1_2026: PortfolioQuarterOrNull;
  q2_2026: PortfolioQuarterOrNull;
  q3_2026: PortfolioQuarterOrNull;
  q4_2026: PortfolioQuarterOrNull;
}

/** asset_contribution_pct: nur q1, KPIs nkm/ebtda/evv. */
export interface AssetContribution {
  q1_2026?: {
    nkm: Record<string, number>;
    ebtda: Record<string, number>;
    evv: Record<string, number>;
  };
}

/**
 * Per-Gesellschaft-KPIs in der Portfolio-Tabelle (Backend shape_portfolio,
 * C2.3a / L-008). ju_abw_pct = (jahr_2026.prognose.ju - jahr_2026.soll.ju)
 * / |jahr_2026.soll.ju| (Betrag im Nenner -> Invertierungs-Fix Verlust-Ges.,
 * Entscheidung Tom-Eric #1). soll.ju == 0/null -> null (graue Ampel).
 */
export interface MemberKpi {
  short_name: string;
  slug: string; // Lookup-Key fuer GET /api/asset/{slug} (Risiko #3)
  ju_prognose: number | null;
  ju_vj: number | null;
  roe_prognose: number | null;
  ju_abw_pct: number | null;
  /** cashflow.prognose.netto_cf (1:1). Enthaelt vorlaeufige Tilgung (D-030). */
  netto_cf_prognose: number | null;
  // R2 Phase 4b (additiv, optional): Bestände + FCF/FCFE-Jahresprognose je Ges.
  /** Bestände je Gesellschaft für die Portfolio-Gesamtübersicht (1:1, null ohne SuSa). */
  bank_bestaende?: BankBestaende | null;
  /** fcfe.jahr.fcf.prognose (1:1). */
  fcf_prognose?: number | null;
  /** fcfe.jahr.fcfe.prognose (1:1). */
  fcfe_prognose?: number | null;
}

/**
 * D-075 / Punkt D: eine Gesellschaft, die im Snapshot steht, aber NICHT zum
 * Portfolio gehört (Curare, Gewerbeenergien). Backend: shape.py `_non_member_kpis`.
 *
 * BEWUSST ANDERE FELDER als `MemberKpi`, kein Aufguss:
 *  - KEIN nkm — Gewerbeenergien hat kein NKM-Konto und der Erzeuger liefert dort
 *    `0.0` (nicht null). Eine NKM-Spalte zeigte „0,00 €" ohne Aussage.
 *  - KEIN ju_abw_pct — eine Soll/Ist-Ampel neben den Immobilien-Gesellschaften
 *    ist eine Aussage, die die Methodik hier nicht deckt.
 */
export interface NonMemberKpi {
  short_name: string;
  slug: string;
  /** Echter Diskriminator seit D-075 — hier immer `false`. */
  is_property_holding: boolean;
  /** `false` ⇒ `bewertung` ist `null` und heißt „nicht anwendbar", NIE 0. */
  valuation_applicable: boolean;
  ju_prognose: number | null;
  ju_vj: number | null;
  ebtda_prognose: number | null;
  netto_cf_prognose: number | null;
  bank_bestaende: BankBestaende | null;
}

/** Kumulierte Jahresbloecke (Σ ueber erlaubtes Subset, C2.1). */
export interface PortfolioCumulative {
  vorjahr: BwaBlock;
  jahr_ist: BwaBlock;
  jahr_prognose: BwaBlock;
  jahr_soll: BwaBlock;
}

/** API-Quartalskeys (Backend verkuerzt q1_2026 -> q1, SCHEMA_MAPPING §6). */
export interface PortfolioQuarterlyApi {
  q1: PortfolioQuarterOrNull;
  q2: PortfolioQuarterOrNull;
  q3: PortfolioQuarterOrNull;
  q4: PortfolioQuarterOrNull;
}

export type ContributionKpis = {
  nkm: Record<string, number>;
  ebtda: Record<string, number>;
  evv: Record<string, number>;
};

/** asset_contribution gefiltert, API-Keys q1..q4 (nur vorhandene Quartale). */
export type PortfolioContribution = Partial<Record<'q1' | 'q2' | 'q3' | 'q4', ContributionKpis>>;

/**
 * D-084 (Prompt 127, Aufgabe 1): Amortisation kumuliert über pph["members"]
 * (Backend shape.py `_amortisation_kumuliert`). Werte NEGATIV wie im Snapshot
 * (Rückführung Bank + GD aus fcfe.jahr); die Kachel zeigt den Betrag.
 * ist/plan/gesamt sind null, sobald einem Mitglied ein Baustein fehlt (keine Teilsumme).
 */
export interface AmortisationKumuliert {
  ist: number | null;
  plan: number | null;
  gesamt: number | null;
  /** meta.data_stand des Snapshots, z. B. „SuSa bis 07/2026". */
  stand: string | null;
}

/**
 * Antwort von GET /api/portfolio (Backend shape_portfolio).
 * MAßGEBLICH ist diese geshapte Form — NICHT die Roh-Snapshot-Struktur
 * (portfolio_property_holding mit q1_2026/quarterly_aggregated). Der Dev-Mock
 * (portfolioFromFixture) bildet exakt diese Form nach.
 */
export interface Portfolio {
  members: MemberKpi[];
  quarterly: PortfolioQuarterlyApi;
  contribution: PortfolioContribution;
  cumulative: PortfolioCumulative;
  /**
   * D-075 / Punkt D, additiv: die Gesellschaften im Snapshot, die NICHT zum
   * Portfolio gehören. Sie fließen in KEINE Portfolio-Größe ein — nicht in
   * `cumulative`, nicht in `quarterly`, nicht in `contribution`.
   * OPTIONAL: ein älterer Backend-Stand liefert den Key noch nicht (dann entfällt
   * die separate Tabelle, statt eine leere zu zeigen).
   */
  non_members?: NonMemberKpi[];
  /** D-084, additiv. OPTIONAL: älterer Backend-Stand ohne den Key → keine Kachel. */
  amortisation_kumuliert?: AmortisationKumuliert;
  /**
   * D-084, additiv: KI-Findings NUR der Portfolio-Mitglieder (Backend filtert auf
   * pph["members"] und sortiert). null = Snapshot ohne das Feld → keine Sektion.
   */
  ki_summary?: KiSummaryEintrag[] | null;
}

/**
 * Vollständige Fixture-Struktur (Dev-Mock). Im Prod-Backend werden meta,
 * portfolio und asset einzeln über die Endpunkte geliefert; die Fixture
 * enthält die Roh-JSON, aus der der Mock-Client diese Sichten ableitet.
 */
export interface SnapshotFixture {
  schema_version: string;
  generated_at: string;
  data_stand: string;
  methodik_version: string;
  source_files: string[];
  entities: Entity[];
  portfolio_property_holding: {
    members: string[];
    quarterly_aggregated: PortfolioQuarterlyAggregated;
    asset_contribution_pct: AssetContribution;
  };
  excluded_entities: ExcludedEntities;
  vollstaendigkeit?: Vollstaendigkeit | null; // additiv, D-047
  ki_summary?: KiSummaryEintrag[]; // additiv, D-084 (alle Entitäten, ungefiltert)
  berichtsjahr?: number | null; // additiv, D-089
  vorjahr?: number | null; // additiv, D-089
  jahreswechsel?: Jahreswechsel | null; // additiv, D-089
}
